import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, TextField, Drawer, IconButton, useTheme } from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import useResponsive from '../../../hooks/useResponsive';
import CustomModal from '../../CustomModal';
import Iconify from '../../Iconify';
import useLocales from '../../../hooks/useLocales';
import { conObjForm } from '../../../utils/connection-assistant';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';

const BirthdayDialog = ({ handleClose, handleOpen }) => {
  const { translate } = useLocales();

  const theme = useTheme();

  const isDesktop = useResponsive('up', 'lg');

  const [loading, setLoading] = useState(false);

  const { data: modalData } = useSelector((state) => state.profile);

  const [birthday, setBirthday] = useState(modalData ? dayjs(modalData) : null);

  const isValidDate = Boolean(birthday);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (modalData) {
      setBirthday(dayjs(modalData));
    }
  }, [modalData]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const formattedDate = birthday ? birthday.format('YYYY-MM-DD') : '';

      const payload = {
        'update_user[dob]': formattedDate,
      };

      const { data: response } = await conObjForm.post('v1/edit-user', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        enqueueSnackbar(response?.message || 'Date of birth updated', {
          variant: 'success',
        });

        handleClose();
      } else {
        enqueueSnackbar(response?.message || 'Edit Date of Birth Failed', { variant: 'error' });
      }
    } catch (error) {
      console.error('STATUS:', error.response?.status);
      console.error('DATA:', error.response?.data);

      const data = error.response?.data;

      if (data?.error && Array.isArray(data.error)) {
        data.error.forEach((err) => {
          enqueueSnackbar(err, { variant: 'error' });
        });
      } else if (data?.message) {
        enqueueSnackbar(data.message, { variant: 'error' });
      } else {
        enqueueSnackbar('An error occurred during submission', {
          variant: 'error',
        });
      }

      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          actions={
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isValidDate}
              fullWidth
              sx={{
                background: theme.palette.gradients.buttonPrimary,
                textTransform: 'none',
                borderRadius: '25px',
                boxShadow: 'none',
                height: 45,
                '&.Mui-disabled': {
                  background: '#F6F7F9',
                },
              }}
            >
              {translate('submit')}
            </Button>
          }
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                py: 2,
                px: 3,
              }}
            >
              <Typography variant="h6" fontWeight={600} textAlign="center">
                {translate('birthday')}
              </Typography>

              <Typography variant="body2" fontWeight={500}>
                {translate('add_your_birthday')}
              </Typography>

              <DatePicker
                value={birthday}
                onChange={(newValue) => setBirthday(newValue)}
                disableFuture
                inputFormat="YYYY/MM/DD"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="YYYY/MM/DD"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: 45,
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                      '& input::placeholder': {
                        color: '#9CA3AF',
                        opacity: 1,
                      },
                      '& input': {
                        padding: '12px 14px',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </LocalizationProvider>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
              <Iconify icon="material-symbols:arrow-back-ios-new-rounded" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('birthday')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              height: '100%',
              // eslint-disable-next-line no-nested-ternary
              background: !isDesktop
                ? isSafariOrIOS
                  ? theme.palette.gradients.theme3Ios
                  : theme.palette.gradients.theme3
                : '#F6F7F9',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                backgroundColor: '#fff',
                p: 2,
                borderRadius: 2,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: '12px',
                  }}
                >
                  <Typography variant="h6" fontWeight={600} textAlign="start">
                    {translate('add_your_birthday')}
                  </Typography>

                  <DatePicker
                    value={birthday}
                    onChange={(newValue) => setBirthday(newValue)}
                    disableFuture
                    fullWidth
                    inputFormat="YYYY/MM/DD"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="YYYY/MM/DD"
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F3F4F6',
                            borderRadius: '12px',
                            height: 45,
                            '& fieldset': {
                              border: 'none',
                            },
                            '&:hover fieldset': {
                              border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                              border: 'none',
                            },
                          },
                          '& input::placeholder': {
                            color: '#9CA3AF',
                            opacity: 1,
                          },
                          '& input': {
                            padding: '12px 14px',
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isValidDate}
              fullWidth
              sx={{
                mt: '30px',
                background: theme.palette.gradients.buttonPrimary,
                textTransform: 'none',
                borderRadius: '25px',
                boxShadow: 'none',
                height: 45,
                '&.Mui-disabled': {
                  background: '#F6F7F9',
                },
              }}
            >
              {translate('submit')}
            </Button>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default BirthdayDialog;
